import { Audience as VideoAudience } from "../types/api/video";
import { Audience as ShowAudience } from "../types/api/show-data";
import { PUBLISHED_TEST_STATION_IDS } from "../constants";

type LocalContentStation = {
  cid: string;
  call_sign: string;
  short_common_name: string;
};

/**
 * Shared filter for both helpers below, so the two lists always agree on *which*
 * stations count as local content - they only differ in what they return.
 * @param {VideoAudience[] | ShowAudience[]} audiences
 * @returns {LocalContentStation[]}
*/
const localContentStationsFromAudiences = (
  audiences: VideoAudience[] | ShowAudience[]
): LocalContentStation[] => {
  const stations = [] as LocalContentStation[];

  audiences.forEach(audience => {
    const isLocal = audience.scope === "local";
    const station = audience.station;
    const notTestStation = !PUBLISHED_TEST_STATION_IDS.includes(station && station.cid || "");

    if (isLocal && notTestStation && station) {
      stations.push(station);
    }
  });

  return stations;
}

/**
 * Takes an Audience object and returns a list of local station short common names.
 * Matching what was done in the pbsorg django codebase
 *
 * NOTE: this list is for *display* - it feeds the user-facing sponsorship text on
 * show and video pages. Do not gate behavior on it; short common names are mutable
 * labels, not identifiers. Use getLocalContentStationIds instead. See RWEB-9977.
 * @param {VideoAudience[] | ShowAudience[]} audiences
 * @returns {string[]}
*/

const getLocalContentStations = (audiences: VideoAudience[] | ShowAudience[]): string[] => {
  const localContentStations = localContentStationsFromAudiences(audiences)
    .map(station => station.short_common_name);

  // Remove duplicates and sort alphabetically
  return Array.from(new Set(localContentStations)).sort()
}

/**
 * Takes an Audience object and returns a list of local station IDs, for comparing
 * against a station ID we already hold (e.g. the SVP's station from the request).
 * Unlike getLocalContentStations, these are stable identifiers, so this is what
 * behavior should key off of. See RWEB-9977.
 * @param {VideoAudience[] | ShowAudience[]} audiences
 * @returns {string[]}
*/

const getLocalContentStationIds = (audiences: VideoAudience[] | ShowAudience[]): string[] => {
  const localContentStationIds = localContentStationsFromAudiences(audiences)
    .map(station => station.cid);

  // Remove duplicates - order is irrelevant, these are only ever compared by lookup
  return Array.from(new Set(localContentStationIds))
}

export { getLocalContentStations, getLocalContentStationIds }
